import { Typography, Box, Tabs, Tab, Paper, IconButton, Tooltip } from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../../redux/store';
import { ApplicationRoles, RoleName } from '../../../types/user';
import DailyUpdates from '../organisms/DailyUpdates/index';
import RefreshIcon from '@mui/icons-material/Refresh';
import { listOrganisationDailyUpdates } from '../../../redux/slices/daily-updates';
import { keyframes } from '@emotion/react';
import { styled } from '@mui/system';

const spin = keyframes`
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
`;

const RefreshIconAnimated = styled(RefreshIcon)(({ isRefreshing }: { isRefreshing: boolean }) => ({
	animation: isRefreshing ? `${spin} 1s linear infinite` : 'none'
}));

const RoleSelectionDailyUpdates = () => {
	const [activeRole, setActiveRole] = useState<string>('');
	const [isRefreshing, setIsRefreshing] = useState(false);
	const user = useAppSelector((state) => state.user.currentUser);
	const organisation = useAppSelector((state) => state.organisation.current);
	const dispatch = useAppDispatch();

	const organisationId = organisation?.id || user?.roles?.find((r) => r.organisationId)?.organisationId;

	const filteredRoles =
		user?.roles.filter((role) => role.roleName !== RoleName.Employee).map((role) => role.roleName) || [];

	useEffect(() => {
		if (filteredRoles.length > 0 && !activeRole) {
			setActiveRole(filteredRoles[0]);
		}
	}, [filteredRoles, activeRole]);

	const handleRefresh = useCallback(() => {
		if (organisationId && user?.id && activeRole) {
			setIsRefreshing(true);
			dispatch(
				listOrganisationDailyUpdates({
					organisationId,
					roleName: activeRole,
					status: null,
					dailyUpdates: true,
					lastLoggedOut: localStorage.getItem(`lastLogout_${user.id}`) || undefined
				})
			).finally(() => {
				setIsRefreshing(false);
			});
		}
	}, [organisationId, user?.id, activeRole, dispatch]);

	const renderDailyUpdates = () => {
		switch (activeRole) {
			case RoleName.AdmissionsOfficer:
				return <DailyUpdates.AdmissionsOfficerDU />;
			case RoleName.Registrar:
				return <DailyUpdates.RegistrarDU />;
			case RoleName.Verifier:
				return <DailyUpdates.VerifierDU />;
			case RoleName.OrgnaisationAdmin:
				return <DailyUpdates.OrganisationAdminDU />;
			case RoleName.Student:
				return <DailyUpdates.StudentDU />;
			default:
				return (
					<Box sx={{ p: 4, textAlign: 'center' }}>
						<Typography variant="body1">No updates available for this role.</Typography>
					</Box>
				);
		}
	};

	return (
		<Box
			sx={{
				width: '100%',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden'
			}}
		>
			{filteredRoles.length > 1 && (
				<Paper
					elevation={0}
					sx={{
						mb: 3,
						borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
						borderRadius: 0,
						flexShrink: 0, // Prevent tabs from shrinking
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center'
					}}
				>
					<Tabs
						value={activeRole}
						onChange={(_, newValue) => setActiveRole(newValue)}
						variant="scrollable"
						scrollButtons="auto"
						textColor="primary"
						indicatorColor="primary"
						centered
						sx={{
							flex: 1,
							'& .MuiTabs-flexContainer': {
								justifyContent: 'center'
							},
							'& .MuiTab-root': {
								textTransform: 'none',
								fontSize: '15px',
								fontWeight: 500,
								px: 3,
								py: 1.5
							}
						}}
					>
						{filteredRoles.map((role) => (
							<Tab
								key={role}
								label={ApplicationRoles[role as keyof typeof ApplicationRoles]}
								value={role}
							/>
						))}
					</Tabs>
					<Tooltip title="Refresh updates">
						<IconButton onClick={handleRefresh} disabled={isRefreshing} sx={{ mr: 2 }}>
							<RefreshIconAnimated isRefreshing={isRefreshing} />
						</IconButton>
					</Tooltip>
				</Paper>
			)}

			{filteredRoles.length === 1 && (
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
					<Tooltip title="Refresh updates">
						<IconButton onClick={handleRefresh} disabled={isRefreshing} sx={{ mr: 2 }}>
							<RefreshIconAnimated isRefreshing={isRefreshing} />
						</IconButton>
					</Tooltip>
				</Box>
			)}

			<Box
				sx={{
					p: 2,
					overflow: 'auto'
				}}
			>
				{renderDailyUpdates()}
			</Box>
		</Box>
	);
};

export default RoleSelectionDailyUpdates;
